<script setup lang="ts">
// The server's version and self-update, for admins. Opening SRV checks for a newer release;
// the server installs one at once and restarts into it, and this page reloads onto it.
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { describeError } from '../api/http'
import type { UpdateStatus } from '../api/types'
import { formatTime } from '../lib/format'
import { useSession } from '../stores/session'

const session = useSession()
const status = ref<UpdateStatus | null>(null)
/** Waiting for the server to come back on the version being installed. */
const restarting = ref<string | null>(null)
let timer: ReturnType<typeof setTimeout> | undefined

async function poll(): Promise<void> {
  try {
    const s = await session.api.updateStatus()
    if (restarting.value && s.version !== status.value?.version) {
      session.toast(`Updated to ${s.version}`)
      location.reload()
      return
    }
    status.value = s
    if (s.installing) restarting.value = s.installing
    else if (restarting.value && !s.checking) restarting.value = null
  } catch {
    // Down while it restarts: keep asking.
    if (!restarting.value) return
  }
  if (restarting.value || status.value?.checking) timer = setTimeout(poll, 2000)
}

async function check(): Promise<void> {
  clearTimeout(timer)
  try {
    status.value = await session.api.checkUpdate()
    timer = setTimeout(poll, 1000)
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

onMounted(check)
onBeforeUnmount(() => clearTimeout(timer))
</script>

<template>
  <section v-if="status" class="border-t border-border px-6 py-5 max-md:px-4" data-testid="update-settings">
    <div class="flex flex-wrap items-baseline justify-between gap-3">
      <h2 class="label mb-0">Version</h2>
      <button
        v-if="!status.disabled"
        class="btn-outline"
        :disabled="status.checking || !!restarting"
        data-testid="update-check"
        @click="check"
      >
        Check now
      </button>
    </div>
    <p class="mt-2 text-2xl font-light tracking-tight" data-testid="update-version">{{ status.version }}</p>
    <p v-if="restarting" class="mono mt-2 text-text-secondary" data-testid="update-installing">
      Installing {{ restarting }} · the server restarts into it, this page reloads when it's back
    </p>
    <p v-else-if="status.checking" class="mono mt-2 text-text-muted">Checking for a newer release…</p>
    <p v-else-if="status.disabled" class="mono mt-2 text-text-muted" data-testid="update-disabled">
      Doesn't update itself: {{ status.disabled }}
    </p>
    <p v-else-if="status.last_error" class="mono mt-2 text-error" data-testid="update-error">
      Last check failed: {{ status.last_error }}
    </p>
    <p v-else-if="status.last_checked" class="mono mt-2 text-text-muted" data-testid="update-current">
      Up to date · checked {{ formatTime(status.last_checked) }}
    </p>
    <p v-if="status.rolled_back" class="mono mt-2 text-error" data-testid="update-rolled-back">
      Update rolled back: {{ status.rolled_back }}
    </p>
    <p class="mono mt-3 text-text-disabled">
      hrth updates itself: it checks for a signed release every hour and when you open this page, installs it and
      restarts in a few seconds. One that doesn't start is rolled back.
    </p>
  </section>
</template>
